// Map an action tool call (from the dj-remote companion via Supabase
// Broadcast, or potentially any other source) onto the existing App.js
// action handlers. Returns the same shape the earlier voice pipeline
// expected so callers don't have to branch.
//
// Contract:
//   handlers: {
//     handlePlayPause, handlePlayNextSong, handlePlayPreviousSong,
//     handleVolumeChange, handleMuteToggle, handleSkipPlaylist,
//     handleDislikeSkip, handleShutdownDJ,
//     submitTrackFeedback: async ({feedbackType, reasons, skip?}) => void,
//     showToast: (message, type?) => void,
//   }
//   state: { isPlaying, isMuted, volume, originalVolume }
//   - originalVolume is a legacy hook from the old local voice flow; the
//     remote-driven flow doesn't duck, so it's usually null.
//
// Principle: every tool case must take the exact same code path the DJ's
// own UI button takes. That guarantees side-effects match — e.g. dislike
// must use handleDislikeSkip (sets skipInitiatedByApp so the skip-logger
// doesn't double-count) rather than the generic handlePlayNextSong.

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function synthVolumeEvent(newVolume) {
  // handleVolumeChange expects a synthetic event with target.value
  return { target: { value: String(clamp01(newVolume)) } };
}

export async function dispatchAction(toolCall, handlers, state) {
  if (!toolCall || !toolCall.name) {
    return { shouldRestoreVolume: true };
  }

  const name = toolCall.name;
  const args = toolCall.args || {};

  switch (name) {
    case 'play':
      if (!state.isPlaying) handlers.handlePlayPause?.();
      return { shouldRestoreVolume: true };

    case 'pause':
      if (state.isPlaying) handlers.handlePlayPause?.();
      return { shouldRestoreVolume: false };

    case 'skip':
      handlers.handlePlayNextSong?.();
      return { shouldRestoreVolume: true };

    case 'previous':
      handlers.handlePlayPreviousSong?.();
      return { shouldRestoreVolume: true };

    case 'volume_up': {
      const amount = typeof args.amount === 'number' ? args.amount : 0.1;
      // Compute against original (pre-duck) volume, not the ducked state.
      const base = typeof state.originalVolume === 'number' ? state.originalVolume : state.volume;
      const target = clamp01(base + amount);
      handlers.handleVolumeChange?.(synthVolumeEvent(target));
      return { shouldRestoreVolume: false, newOriginalVolume: target };
    }

    case 'volume_down': {
      const amount = typeof args.amount === 'number' ? args.amount : 0.1;
      const base = typeof state.originalVolume === 'number' ? state.originalVolume : state.volume;
      const target = clamp01(base - amount);
      handlers.handleVolumeChange?.(synthVolumeEvent(target));
      return { shouldRestoreVolume: false, newOriginalVolume: target };
    }

    case 'set_volume': {
      // Exact value from a slider on the remote (args.volume is 0..1).
      const v = typeof args.volume === 'number' ? args.volume : null;
      if (v === null) return { shouldRestoreVolume: true };
      const target = clamp01(v);
      handlers.handleVolumeChange?.(synthVolumeEvent(target));
      return { shouldRestoreVolume: false, newOriginalVolume: target };
    }

    case 'mute':
      if (!state.isMuted) handlers.handleMuteToggle?.();
      return { shouldRestoreVolume: false };

    case 'unmute':
      if (state.isMuted) handlers.handleMuteToggle?.();
      return { shouldRestoreVolume: true };

    case 'skip_playlist':
      handlers.showToast?.('Picking a new playlist…', 'info');
      handlers.handleSkipPlaylist?.();
      return { shouldRestoreVolume: true };

    case 'like_track':
      if (handlers.submitTrackFeedback) {
        try {
          await handlers.submitTrackFeedback({
            feedbackType: 'like',
            reasons: args.reason ? [args.reason] : ['Other'],
          });
        } catch (err) {
          console.warn('[action] like_track feedback failed:', err);
        }
      }
      return { shouldRestoreVolume: true };

    case 'dislike_track':
      // Skip first so the song moves forward immediately. /submit-track-feedback
      // runs feedback_agent.evaluate() (a multi-second GPT call) before
      // returning; awaiting it here used to stall the track change (DJ-130).
      // The regen response still routes through handleFeedbackRegen inside
      // submitTrackFeedback when the POST resolves.
      if (args.skip !== false) {
        if (handlers.handleDislikeSkip) handlers.handleDislikeSkip();
        else handlers.handlePlayNextSong?.();
      }
      if (handlers.submitTrackFeedback) {
        Promise.resolve(handlers.submitTrackFeedback({
          feedbackType: 'dislike',
          reasons: args.reason ? [args.reason] : ['Other'],
        })).catch((err) => {
          console.warn('[action] dislike_track feedback failed:', err);
        });
      }
      return { shouldRestoreVolume: true };

    case 'shutdown_dj':
      // Full session shutdown — user said "shut down", "stop everything",
      // "I'm done", etc. Mirrors clicking the Shut Down DJ button.
      handlers.showToast?.('Shutting down DJ…', 'info');
      handlers.handleShutdownDJ?.();
      return { shouldRestoreVolume: true };

    case 'start_dj':
      // Power the DJ back on after a shutdown — counterpart to shutdown_dj.
      // Lands on the user-scoped broadcast channel so it survives session
      // rotation. Mirrors clicking the master-toggle Power button.
      handlers.showToast?.('Starting DJ…', 'info');
      handlers.handleStartDJ?.();
      return { shouldRestoreVolume: true };

    case 'no_action':
      return { shouldRestoreVolume: true };

    default:
      console.warn('[action] Unknown tool:', name);
      return { shouldRestoreVolume: true };
  }
}
